import { useEffect, useState } from "react";
import { api, money } from "@/api";
import { CoachCard } from "@/components/CoachCard";
import { Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";

export default function Browse() {
  const [coaches, setCoaches] = useState([]);
  const [filters, setFilters] = useState({ cities: [], levels: [], specialties: [] });
  const [f, setF] = useState({ q: "", city: "", level: "", specialty: "", category: "", min_experience: 0, max_price: 0 });
  const [compare, setCompare] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/coaches/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/coaches", { params }).then((r) => setCoaches(r.data)).finally(() => setLoading(false));
  }, [f]);

  const toggleCompare = (coach) =>
    setCompare((prev) => prev.find((c) => c.id === coach.id)
      ? prev.filter((c) => c.id !== coach.id)
      : prev.length >= 2 ? [prev[1], coach] : [...prev, coach]);

  const Select = ({ label, value, onChange, options, testid }) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#121212] border border-white/10 text-sm text-white px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200"
      >
        <option value="">Any</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );

  return (
    <div data-testid="browse-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <div className="mb-12 max-w-2xl">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]">Marketplace</span>
        <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Find your goalie's team</h1>
        <p className="text-sm text-zinc-400 mt-4 leading-relaxed">Goalie coaches plus the off-ice team around them — strength, mobility, vision and mental performance. Tick two to compare them head-to-head.</p>
      </div>

      <div data-testid="category-tabs" className="flex flex-wrap gap-2 mb-10">
        {[{ v: "", l: "All trainers" }, ...(filters.categories || []).map((c) => ({ v: c, l: c }))].map((c) => (
          <button
            key={c.v || "all"}
            data-testid={`category-tab-${c.v ? c.v.toLowerCase().replace(/[^a-z]+/g, "-") : "all"}`}
            onClick={() => setF({ ...f, category: c.v })}
            className={`text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors duration-200 ${
              f.category === c.v ? "border-[#00e5ff] text-[#00e5ff] bg-[#00e5ff]/5" : "border-white/10 text-zinc-400 hover:border-white/40"
            }`}
          >
            {c.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-3 space-y-6 lg:sticky lg:top-24 self-start">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400">
            <SlidersHorizontal className="w-4 h-4 text-[#00e5ff]" /> Filters
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Search</span>
            <input
              data-testid="filter-search-input"
              value={f.q}
              onChange={(e) => setF({ ...f, q: e.target.value })}
              placeholder="Name, city, keyword"
              className="mt-2 w-full bg-[#121212] border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200"
            />
          </label>
          <Select label="City" testid="filter-city-select" value={f.city} options={filters.cities} onChange={(v) => setF({ ...f, city: v })} />
          <Select label="Level" testid="filter-level-select" value={f.level} options={filters.levels} onChange={(v) => setF({ ...f, level: v })} />
          <Select label="Specialty" testid="filter-specialty-select" value={f.specialty} options={filters.specialties} onChange={(v) => setF({ ...f, specialty: v })} />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Min experience: {f.min_experience} yrs</span>
            <input data-testid="filter-experience-range" type="range" min="0" max="20" value={f.min_experience}
              onChange={(e) => setF({ ...f, min_experience: Number(e.target.value) })}
              className="mt-3 w-full accent-[#00e5ff]" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Max hourly: {f.max_price ? money(f.max_price) : "Any"}</span>
            <input data-testid="filter-price-range" type="range" min="0" max="200" step="5" value={f.max_price}
              onChange={(e) => setF({ ...f, max_price: Number(e.target.value) })}
              className="mt-3 w-full accent-[#00e5ff]" />
          </label>
          <button data-testid="filter-reset-btn" onClick={() => setF({ q: "", city: "", level: "", specialty: "", category: "", min_experience: 0, max_price: 0 })}
            className="text-[10px] uppercase tracking-widest text-[#ff3b30] hover:underline">Reset filters</button>
        </aside>

        <div className="lg:col-span-9">
          <p data-testid="results-count" className="text-xs uppercase tracking-widest text-zinc-500 mb-6">
            {loading ? "Loading…" : `${coaches.length} coach${coaches.length === 1 ? "" : "es"} available`}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {coaches.map((c) => (
              <CoachCard key={c.id} coach={c} selected={!!compare.find((x) => x.id === c.id)} onCompare={toggleCompare} />
            ))}
          </div>
          {!loading && coaches.length === 0 && (
            <p data-testid="no-results" className="text-sm text-zinc-500 border border-white/10 p-10">No coaches match those filters yet.</p>
          )}
        </div>
      </div>

      {compare.length > 0 && (
        <div data-testid="compare-tray" className="fixed bottom-0 left-0 right-0 z-40 bg-black/85 backdrop-blur-xl border-t border-[#00e5ff]/40">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {[0, 1].map((i) => {
              const c = compare[i];
              return (
                <div key={i} className="min-h-[64px]">
                  {c ? (
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="display uppercase font-bold text-lg leading-none">{c.name}</p>
                        <p className="text-[11px] text-zinc-400 mt-1">{c.city} · {c.experience_years} yrs · ★ {c.rating} · from {money(c.hourly_from)}</p>
                        <p className="text-[11px] text-zinc-500 mt-1">{c.specialties.join(", ")}</p>
                      </div>
                      <button data-testid={`compare-remove-${c.id}`} onClick={() => toggleCompare(c)} className="text-zinc-500 hover:text-[#ff3b30] transition-colors duration-200"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <p className="text-[11px] uppercase tracking-widest text-zinc-600">Pick a second coach to compare</p>
                  )}
                </div>
              );
            })}
            <div className="flex md:justify-end gap-3">
              {compare[0] && (
                <Link to={`/coaches/${compare[0].id}`} data-testid="compare-book-first" className="bg-[#00e5ff] text-black text-[11px] font-bold uppercase px-5 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Book {compare[0].name.split(" ")[0]}</Link>
              )}
              {compare[1] && (
                <Link to={`/coaches/${compare[1].id}`} data-testid="compare-book-second" className="border border-white/25 text-[11px] uppercase px-5 py-3 hover:border-[#00e5ff] hover:text-[#00e5ff] transition-colors duration-200">Book {compare[1].name.split(" ")[0]}</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
